import React, { useState, useEffect } from 'react';
import './App.css';
import portrait from './assets/portrait.jpg';

function App() {
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'experience', 'skills', 'education'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const experiences = [
    {
      company: "Micro1, Jammu, Ind",
      period: "Aug 2023-Present",
      role: "Lead AI Engineer, AI Team",
      projects: [
        {
          name: "Flowcharts Extraction",
          description: "Developed a recursive Algorithm to automatically extract the flowcharts from a complex NCCN document with many complicated edge cases and save them in a tree structure.",
          tech: "Python, SQL"
        },
        {
          name: "RAG",
          description: "Led the development of AI search which enabled searching for candidates with natural language and increased efficiency by 50%",
          tech: "Python, OpenAI, LangChain, FastAPI, AWS"
        },
        {
          name: "AI Interviewer",
          description: "Led the development of AI Interviewer which took realtime interviews of 50k candidates per month and streamlined the entire hiring process.",
          tech: "Python, OpenAI, Deepgram, Livekit, AWS"
        },
        {
          name: "Research",
          description: "Orchestrated the development of open source fine-tuned models and contributed to next-gen human data platforms.",
          tech: "Python, GCP"
        }
      ]
    },
    {
      company: "Stantech AI, Gurugram, Ind",
      period: "Dec 2022-Aug 2023",
      role: "Lead Software Engineer, Backend Team",
      projects: [
        {
          name: "External Dashboard",
          description: "Led the backend team to prepare the entire backend for our clients from scratch which involved a lot of graphs, database integration and data science.",
          tech: "Python, MongoDB, Django, Js"
        },
        {
          name: "Superon",
          description: "Designed several apis to digitize the parent company of Superon",
          tech: "Python, FastApi, MongoDB"
        }
      ]
    },
    {
      company: "Gigaforce IO, Noida, Ind",
      period: "July 2021-Dec 2022",
      role: "Senior Data Scientist, Data Team",
      projects: [
        {
          name: "Subro Scoring Model",
          description: "Designed a DL model on GPU to Classify claim as high, medium and low subro potential and also make a Flask/Django API to deploy the model.",
          tech: "Python, Django, TensorFlow, Scikit-learn"
        },
        {
          name: "Explainable AI",
          description: "Design a dashboard and returned the text summary which was displayed in frontend using Flask API and results were saved in NOSQL database",
          tech: "Python, TensorFlow, Scikit-learn, Pytorch"
        }
      ]
    },
    {
      company: "Read Ink Inc, Bangalore, Ind",
      period: "Feb 2021-July 2021",
      role: "Senior AI Engineer, OCR Team",
      projects: [
        {
          name: "Character Box",
          description: "Designed an algorithm to draw boxes around characters written in cursive",
          tech: "Python, Django, Matlab, ML"
        },
        {
          name: "Recognition Engine",
          description: "Developed several rules for recognition engine and found character probabilities using ML algorithms such as SRVF",
          tech: "Python, ML"
        }
      ]
    },
    {
      company: "Apple Inc, Austin, US",
      period: "Oct 2019 - Jan 2021",
      role: "Senior Software Engineer, Central Workflow Automation and Maintenance Team",
      projects: [
        {
          name: "Webpage development",
          description: "Enabled automated assignment of support tickets to employees. Designed and developed dashboards to track pending tasks and common errors",
          tech: "Python, TensorFlow"
        },
        {
          name: "System upgrades / testing",
          description: "Resolved alerts from automated monitoring systems and WW Customer service",
          tech: "Python, Linux"
        },
        {
          name: "Skywalk Team - Predicts delivery dates for Apple Online Store Orders",
          description: "Built ML engine in online delivery, based on updated business parameters",
          tech: "Python, Django, JAVA, Linux"
        }
      ]
    },
    {
      company: "Contec Inc, San Jose, US",
      period: "Jul 2018 – Sep 2019",
      role: "Software Engineer, Hardware Software Design and Integration Team",
      projects: [
        {
          name: "Designed file transfer application",
          description: "Collaborated with product architects and technical leaders to conceptualize application architecture using SDLC/CI for development, testing and release. Implemented application interface and load-balancer service to efficiently manage file transfers.",
          tech: "Python, scikit-learn, MySQL, Linux"
        },
        {
          name: "System Re-design for service downtime detection",
          description: "Created Graph based data structure to map out all interdependencies between services and developed an algorithm to pinpoint problematic services to streamline the systems i.e., reduced downtime by 200 percent",
          tech: "Python, Tensorflow, Linux"
        }
      ]
    }
  ];

  const skills = {
    "Languages": ["Python", "JavaScript", "Java", "SQL"],
    "AI/ML": ["TensorFlow", "PyTorch", "OpenAI", "LangChain", "Deepgram", "Scikit-learn"],
    "Frameworks": ["FastAPI", "Django", "Flask", "React", "Livekit"],
    "Databases": ["MongoDB", "MySQL", "NOSQL"],
    "Tools & Tech": ["AWS", "GCP", "Linux", "Git", "Jira", "Agile", "MATLAB", "Mathematica", "Selenium"]
  };

  return (
    <div className="App">
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">Aruj Mahajan</div>
          <div className="nav-links">
            <button 
              className={activeSection === 'hero' ? 'active' : ''} 
              onClick={() => scrollToSection('hero')}
            >
              Home
            </button>
            <button 
              className={activeSection === 'experience' ? 'active' : ''} 
              onClick={() => scrollToSection('experience')}
            >
              Experience
            </button>
            <button 
              className={activeSection === 'skills' ? 'active' : ''} 
              onClick={() => scrollToSection('skills')}
            >
              Skills
            </button>
            <button 
              className={activeSection === 'education' ? 'active' : ''} 
              onClick={() => scrollToSection('education')}
            >
              Education
            </button>
          </div>
        </div>
      </nav>

      <div className="navbar-spacer"></div>

      <section id="hero" className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Hi, I'm <span className="gradient-text">Aruj Mahajan</span>
            </h1>
            <h2 className="hero-subtitle">Lead AI Engineer & Software Developer</h2>
            <p className="hero-description">
              Passionate about building intelligent systems and scalable solutions. 
              Experienced in AI/ML, backend development, and delivering impactful products.
            </p>
            <div className="hero-contact">
              <a href="mailto:arujmahajan900@gmail.com" className="contact-btn">
                <span className="icon">✉</span> arujmahajan900@gmail.com
              </a>
              <a href="tel:+918899552580" className="contact-btn">
                <span className="icon">📱</span> +91 88995 52580
              </a>
            </div>
            <div className="hero-location">
              <span className="icon">📍</span> Channi Himmat, Jammu, J&K
            </div>
          </div>
          <div className="hero-image">
            <div className="image-container">
              <img src={portrait} alt="Aruj Mahajan" className="portrait" />
              <div className="image-border"></div>
            </div>
          </div>
        </div>
        <div className="scroll-indicator" onClick={() => scrollToSection('experience')}>
          <div className="mouse"></div>
          <span>Scroll to explore</span>
        </div>
      </section>

      <section id="experience" className="experience-section">
        <div className="section-container">
          <h2 className="section-title">Professional Experience</h2>
          <div className="timeline">
            {experiences.map((exp, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-marker"></div>
                <div className="timeline-content">
                  <div className="experience-header">
                    <div>
                      <h3 className="company-name">{exp.company}</h3>
                      <h4 className="role-name">{exp.role}</h4>
                    </div>
                    <span className="period">{exp.period}</span>
                  </div>
                  <div className="projects-grid">
                    {exp.projects.map((project, pIndex) => (
                      <div key={pIndex} className="project-card">
                        <h5 className="project-name">{project.name}</h5>
                        <p className="project-description">{project.description}</p>
                        <div className="tech-tags">
                          {project.tech.split(',').map((tech, tIndex) => (
                            <span key={tIndex} className="tech-tag">{tech.trim()}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="skills" className="skills-section">
        <div className="section-container">
          <h2 className="section-title">Technical Skills</h2>
          <div className="skills-grid">
            {Object.entries(skills).map(([category, skillList], index) => (
              <div key={index} className="skill-category">
                <h3 className="category-title">{category}</h3>
                <div className="skill-tags">
                  {skillList.map((skill, sIndex) => (
                    <span key={sIndex} className="skill-tag">{skill}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="education" className="education-section">
        <div className="section-container">
          <h2 className="section-title">Education & Publications</h2>
          <div className="education-grid">
            <div className="education-card">
              <div className="degree-icon">🎓</div>
              <h3>Master of Science in Physics</h3>
              <h4>Northwestern University</h4>
              <p className="year">2016 – 2017</p>
              <p className="gpa">GPA: 3.70 | Research Assistant</p>
            </div>
            <div className="education-card">
              <div className="degree-icon">🎓</div>
              <h3>Bachelor of Science in Physics</h3>
              <h4>University of Delhi</h4>
              <p className="year">2012 – 2015</p>
              <p className="gpa">GPA: 4.00</p>
            </div>
          </div>
          
          <div className="publications">
            <h3 className="publications-title">Research Publications</h3>
            <div className="publications-list">
              <a 
                href="https://arxiv.org/abs/2507.16835" 
                target="_blank" 
                rel="noopener noreferrer"
                className="publication-link"
              >
                <span className="icon">📄</span>
                <span>Research Paper 1 - arXiv:2507.16835</span>
                <span className="external-icon">↗</span>
              </a>
              <a 
                href="https://arxiv.org/abs/2507.02869" 
                target="_blank" 
                rel="noopener noreferrer"
                className="publication-link"
              >
                <span className="icon">📄</span>
                <span>Research Paper 2 - arXiv:2507.02869</span>
                <span className="external-icon">↗</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-content">
          <p>&copy; 2025 Aruj Mahajan. Built with React.</p>
          <div className="footer-links">
            <a href="mailto:arujmahajan900@gmail.com">Email</a>
            <a href="tel:+918899552580">Phone</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;

